import { useEffect, useMemo, useState } from 'react';
import { Mic, MicOff } from 'lucide-react';
import TopNav from '@/components/TopNav';
import { AuthorSignature } from '@/components/AuthorSignature';
import { TunerGauge } from '@/components/tuner/TunerGauge';
import { InstrumentSelect, ReferenceControl } from '@/components/tuner/TunerControls';
import { ReferencePanel } from '@/components/tuner/ReferencePanel';
import { PitchHistory } from '@/components/tuner/PitchHistory';
import { TunerClick } from '@/components/tuner/TunerClick';
import { SharedTuningBanner } from '@/components/tuner/SharedTuningBanner';
import { Toaster, toast } from '@/components/ui/sonner';
import { addCustomTuning } from '@/hooks/useTunings';
import { useNavigate } from 'react-router-dom';
import { readSharedTuning, readSharedKit, readSharedExercise, clearSharedTuningParam } from '@/lib/tuningShare';
import { savePercNotes } from '@/components/tuner/PercussionPanel';
import { IntervalPill } from '@/components/tuner/IntervalPill';
import { droneKey } from '@/lib/droneChords';
import { useDrone } from '@/hooks/useDrone';
import { usePitchDetector } from '@/hooks/usePitchDetector';
import { describePitch, describeInterval, findInstrument, freqToMidiFloat, pitchClass } from '@/lib/pitch';

const load = (k, d) => localStorage.getItem(k) ?? d;

const STATUS_TEXT = {
  idle: 'Microfono spento',
  listening: 'In ascolto…',
  suspended: 'Tocca lo schermo per attivare il microfono',
  denied: 'Permesso microfono negato. Abilitalo dalle impostazioni del browser.',
  error: 'Microfono non disponibile su questo dispositivo.',
};

export default function TunerPage() {
  const [instrumentId, setInstrumentId] = useState(() => load('tuner.instrument', 'guitar'));
  const [a4, setA4] = useState(() => Number(load('tuner.a4', 440)));
  const instrument = findInstrument(instrumentId);
  const drone = useDrone({ a4 });
  const { status, freq, start, stop } = usePitchDetector({ echoCancellation: drone.active });
  const listening = status === 'listening' || status === 'suspended';
  const [shared, setShared] = useState(() => readSharedTuning(window.location.search) || readSharedKit(window.location.search));
  const [tuningsVersion, setTuningsVersion] = useState(0);
  const dismissShared = () => { setShared(null); clearSharedTuningParam(); };

  useEffect(() => { localStorage.setItem('tuner.instrument', instrumentId); }, [instrumentId]);
  useEffect(() => { localStorage.setItem('tuner.a4', String(a4)); }, [a4]);
  useEffect(() => { start(); }, [start]);

  const pitch = useMemo(
    () => (freq ? describePitch(freq, a4, instrument.semitones, instrument.group.flats) : null),
    [freq, a4, instrument],
  );
  const inTune = !!pitch && Math.abs(pitch.cents) <= 5;
  const transposing = instrument.semitones !== 0;
  // Old exercise links pointed here: the exercises now live on the Bordone page.
  const navigate = useNavigate();
  useEffect(() => {
    if (readSharedExercise(window.location.search)) navigate(`/bordone${window.location.search}`, { replace: true });
  }, [navigate]);
  const interval = drone.active && freq ? describeInterval(freqToMidiFloat(freq, a4), drone.concertMidi, drone.just) : null;

  return (
    <div className={`relative min-h-screen text-white ${drone.active ? "pb-24" : "pb-8"}`} data-testid="tuner-root">
      <div className="bg-foam" />
      <div className="bg-grain" />
      <div className="relative z-10 max-w-6xl mx-auto px-3 sm:px-4 md:px-8 py-3 md:py-6 flex flex-col gap-4 sm:gap-6">
        <TopNav />

        <header className="px-1">
          <div className="text-[10px] uppercase tracking-[0.4em] text-white/40">Chromatic Tuner · {instrument.group.label}</div>
          <h1 className="font-bebas text-4xl sm:text-5xl lg:text-6xl leading-none mt-1">Tuner<span className="text-pink-400">.</span></h1>
        </header>

        {shared && (
          <SharedTuningBanner
            shared={shared}
            onAccept={() => {
              if (shared.kind === 'kit') {
                savePercNotes(shared.instrumentId, shared.notes);
                toast.success(`Note ${findInstrument(shared.instrumentId).name} importate`);
              } else {
                const added = addCustomTuning(shared.instrumentId, shared);
                toast.success(added ? `Accordatura "${shared.name}" aggiunta` : `"${shared.name}" è già tra le tue accordature`);
              }
              setInstrumentId(shared.instrumentId);
              setTuningsVersion((v) => v + 1);
              dismissShared();
            }}
            onDismiss={dismissShared}
          />
        )}

        <section className="bg-[#111112] border border-white/5 rounded-2xl p-4 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-end">
          <InstrumentSelect value={instrumentId} onChange={setInstrumentId} />
          <ReferenceControl value={a4} onChange={setA4} />
          <button
            data-testid="tuner-mic-toggle"
            onClick={listening ? stop : start}
            className={`h-12 px-5 rounded-full flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold active:scale-95 transition-[transform,background-color] ${listening ? 'bg-white/10 text-white hover:bg-white/15' : 'bg-pink-400 text-black hover:bg-pink-300'}`}
          >
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            {listening ? 'Stop' : 'Start'}
          </button>
        </section>

        <section className="bg-[#111112] border border-white/5 rounded-2xl px-4 py-8 sm:py-12 flex flex-col items-center gap-6">
          <div data-testid="tuner-status" className={`flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] ${listening ? 'text-emerald-400' : 'text-white/40'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${listening ? 'bg-emerald-400 animate-pulse' : 'bg-white/30'}`} />
            {STATUS_TEXT[status]}
          </div>

          <div className="flex items-start leading-none">
            <span
              data-testid="tuner-note"
              className={`font-bebas text-[9rem] sm:text-[12rem] tracking-tight transition-colors duration-150 ${!pitch ? 'text-white/15' : inTune ? 'text-emerald-400 drop-shadow-[0_0_30px_rgba(52,211,153,0.5)]' : 'text-white'}`}
            >
              {pitch ? pitch.note : '--'}
            </span>
            {pitch && <span data-testid="tuner-octave" className="font-bebas text-4xl sm:text-5xl text-white/40 mt-6">{pitch.octave}</span>}
          </div>

          <TunerGauge cents={pitch?.cents ?? 0} active={!!pitch} inTune={inTune} />
          <PitchHistory cents={pitch ? pitch.cents : null} />

          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-white/50 font-mono">
            <span data-testid="tuner-hz">{freq ? `${freq.toFixed(1)} Hz` : '— Hz'}</span>
            <span data-testid="tuner-cents">{pitch ? `${pitch.cents > 0 ? '+' : ''}${pitch.cents} cent` : '— cent'}</span>
            {transposing && pitch && (
              <span data-testid="tuner-concert-note">Concert: {pitch.concert}{pitch.concertOctave}</span>
            )}
          </div>
          {drone.active && (
            <IntervalPill interval={interval} label={drone.cfg.transpose && drone.cfg.transpose !== 'C' ? `${drone.label} (bordone in ${droneKey(drone.cfg.transpose).label})` : drone.label} />
          )}
        </section>

        <ReferencePanel instrument={instrument} a4={a4} freq={freq} tuningsVersion={tuningsVersion} />

        <TunerClick />

        <footer className="mt-6 mb-4"><AuthorSignature /></footer>
      </div>
      <Toaster position="bottom-right" />
    </div>
  );
}
